import { parsePhoneNumberFromString, type CountryCode } from "libphonenumber-js";
import { IANAZone } from "luxon";
import { z } from "zod";

export const uuid = z.uuid();

/** Trimmed non-empty string with a maximum length. */
export const text = (max: number) => z.string().trim().min(1).max(max);
/** Optional free text: empty string becomes null. */
export const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullish()
    .transform((v) => (v ? v : null));

export const email = z.string().trim().toLowerCase().pipe(z.email().max(254));
export const optionalEmail = z
  .string()
  .trim()
  .toLowerCase()
  .nullish()
  .transform((v) => (v ? v : null))
  .pipe(z.email().max(254).nullable());

export const countryCode = z.string().regex(/^[A-Z]{2}$/, "Use a two-letter ISO country code");
export const currencyCode = z.string().regex(/^[A-Z]{3}$/, "Use a three-letter ISO currency code");
export const hexColor = z.string().regex(/^#[0-9a-fA-F]{6}$/, "Use a colour like #1E88E5");
export const timeZone = z.string().refine((tz) => IANAZone.isValidZone(tz), "Unknown time zone");
export const isoDate = z.iso.date();
export const isoDateTime = z.iso.datetime({ offset: true });
/** Wall-clock time "HH:mm" (24h). */
export const wallTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Use HH:mm");
/** Money in minor units (paise, cents). Never floats. */
export const moneyMinor = z.number().int().min(0).max(1_000_000_000_00);

/**
 * Normalises a phone number to E.164 using the clinic's country as the default
 * (so "98765 43210" in an Indian clinic becomes +919876543210). Returns null if invalid.
 */
export function normalizePhone(input: string, defaultCountry: string): string | null {
  const parsed = parsePhoneNumberFromString(input.trim(), defaultCountry as CountryCode);
  return parsed?.isValid() ? parsed.number : null;
}

/** Phone input as typed by staff; the API normalises it with the tenant's country. */
export const phoneInput = z
  .string()
  .trim()
  .max(30)
  .nullish()
  .transform((v) => (v ? v : null));

export const paginationQuery = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(25),
  cursor: z.string().max(200).optional(),
});

export interface Page<T> {
  data: T[];
  page: { limit: number; nextCursor: string | null };
}

/** Stable machine-readable error codes. The UI maps them to messages. */
export const ERROR_CODES = [
  "bad_request",
  "validation_failed",
  "unauthenticated",
  "mfa_required",
  "forbidden",
  "not_found",
  "conflict",
  "stale_version",
  "rate_limited",
  "tenant_suspended",
  "entitlement_exceeded",
  "internal",
] as const;
export type ErrorCode = (typeof ERROR_CODES)[number];

export interface ApiErrorBody {
  error: { code: ErrorCode; message: string; details?: unknown; requestId?: string };
}
